<script setup>
import { computed, ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputNumber from 'primevue/inputnumber'
import AutoComplete from 'primevue/autocomplete'
import Checkbox from 'primevue/checkbox'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  discount: {
    type: Object,
    default: null
  },
  users: {
    type: Array,
    default: () => []
  },
  brands: {
    type: Array,
    default: () => []
  },
  mode: {
    type: String,
    default: 'create'
  },
  submitting: {
    type: Boolean,
    default: false
  },
  fieldErrors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['close', 'submit'])

const discountPercent = ref(null)
const isActive = ref(true)
const isGlobal = ref(false)

const userSelected = ref(null)
const userLastQuery = ref('')
const filteredUsers = ref([])

const brandSelected = ref(null)
const brandLastQuery = ref('')
const filteredBrands = ref([])

const userId = computed(() =>
  userSelected.value && typeof userSelected.value === 'object' ? userSelected.value.id : null
)
const brandId = computed(() =>
  brandSelected.value && typeof brandSelected.value === 'object' ? brandSelected.value.id : null
)

const localErrors = ref({
  discountPercent: '',
  userId: '',
  brandId: ''
})

const dialogTitle = computed(() =>
  props.mode === 'edit' ? 'Редактирование скидки' : 'Создание скидки'
)

const submitLabel = computed(() =>
  props.mode === 'edit' ? 'Сохранить' : 'Создать'
)

function clearLocalErrors() {
  localErrors.value = {
    discountPercent: '',
    userId: '',
    brandId: ''
  }
}

function resetForm() {
  discountPercent.value = null
  isActive.value = true
  userSelected.value = null
  userLastQuery.value = ''
  brandSelected.value = null
  brandLastQuery.value = ''
  isGlobal.value = false
  clearLocalErrors()
}

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      discountPercent.value = props.discount?.discountPercent ?? null
      isActive.value = props.discount?.isActive ?? true
      isGlobal.value = props.discount?.brandId == null && props.discount != null
      userSelected.value = props.users.find(u => u.id === props.discount?.userId) ?? null
      userLastQuery.value = ''
      brandSelected.value = props.brands.find(b => b.id === props.discount?.brandId) ?? null
      brandLastQuery.value = ''
      clearLocalErrors()
    } else {
      resetForm()
    }
  }
)

watch(
  () => props.discount,
  (discount) => {
    if (props.visible) {
      discountPercent.value = discount?.discountPercent ?? null
      isActive.value = discount?.isActive ?? true
      isGlobal.value = discount?.brandId == null && discount != null
      userSelected.value = props.users.find(u => u.id === discount?.userId) ?? null
      userLastQuery.value = ''
      brandSelected.value = props.brands.find(b => b.id === discount?.brandId) ?? null
      brandLastQuery.value = ''
    }
  }
)

watch(isGlobal, (value) => {
  if (value) {
    brandSelected.value = null
    brandLastQuery.value = ''
    localErrors.value.brandId = ''
  }
})

function getFieldError(field) {
  return (
    localErrors.value[field] ||
    props.fieldErrors?.[field] ||
    props.fieldErrors?.[field.charAt(0).toUpperCase() + field.slice(1)] ||
    ''
  )
}

function hasFieldError(field) {
  return Boolean(getFieldError(field))
}

function validateForm() {
  clearLocalErrors()

  if (!userId.value) {
    localErrors.value.userId = userLastQuery.value.trim()
      ? `Пользователь «${userLastQuery.value}» не найден`
      : 'Выберите пользователя'
  }

  if (!isGlobal.value && !brandId.value) {
    localErrors.value.brandId = brandLastQuery.value.trim()
      ? `Бренд «${brandLastQuery.value}» не найден`
      : 'Выберите бренд или включите "На все бренды"'
  }

  if (
    discountPercent.value == null ||
    discountPercent.value <= 0 ||
    discountPercent.value > 100
  ) {
    localErrors.value.discountPercent = 'Скидка должна быть от 1 до 100'
  } else if (!Number.isInteger(Number(discountPercent.value))) {
    localErrors.value.discountPercent = 'Скидка должна быть целым числом'
  }

  return !Object.values(localErrors.value).some(Boolean)
}

function submitForm() {
  if (!validateForm()) return

  emit('submit', {
    discountPercent: Math.round(Number(discountPercent.value)),
    isActive: isActive.value,
    userId: userId.value,
    brandId: isGlobal.value ? null : brandId.value
  })
}

function filterUsers(event) {
  const q = (event.query ?? '').toLowerCase().trim()
  userLastQuery.value = q
  filteredUsers.value = q
    ? props.users.filter(u => u.email?.toLowerCase().includes(q))
    : [...props.users]
}

function filterBrands(event) {
  const q = (event.query ?? '').toLowerCase().trim()
  brandLastQuery.value = q
  filteredBrands.value = q
    ? props.brands.filter(b => b.name?.toLowerCase().includes(q))
    : [...props.brands]
}
</script>

<template>
  <Dialog
    :visible="props.visible"
    modal
    :header="dialogTitle"
    :style="{ width: 'min(500px, 95vw)' }"
    @update:visible="emit('close')"
  >
    <div class="form">
      <div class="field">
        <label for="discount-user">B2B пользователь</label>
        <AutoComplete
          id="discount-user"
          v-model="userSelected"
          :suggestions="filteredUsers"
          optionLabel="email"
          placeholder="Введите email"
          class="w-full"
          :invalid="hasFieldError('userId')"
          @complete="filterUsers"
          @item-select="userLastQuery = ''; localErrors.userId = ''"
          @clear="userLastQuery = ''; localErrors.userId = ''"
        />
        <small v-if="hasFieldError('userId')" class="error">
          {{ getFieldError('userId') }}
        </small>
      </div>

      <div class="row">
        <Checkbox
          v-model="isGlobal"
          inputId="discount-global"
          binary
        />
        <label for="discount-global">На все бренды</label>
      </div>

      <div class="field">
        <label for="discount-brand">Бренд</label>
        <AutoComplete
          id="discount-brand"
          v-model="brandSelected"
          :suggestions="filteredBrands"
          optionLabel="name"
          placeholder="Введите название бренда"
          class="w-full"
          :disabled="isGlobal"
          :invalid="hasFieldError('brandId')"
          @complete="filterBrands"
          @item-select="brandLastQuery = ''; localErrors.brandId = ''"
          @clear="brandLastQuery = ''; localErrors.brandId = ''"
        />
        <small v-if="hasFieldError('brandId')" class="error">
          {{ getFieldError('brandId') }}
        </small>
      </div>

      <div class="field">
        <label for="discount-percent">Размер скидки</label>
        <InputNumber
          id="discount-percent"
          v-model="discountPercent"
          suffix="%"
          :min="1"
          :max="100"
          :step="1"
          :minFractionDigits="0"
          :maxFractionDigits="0"
          :invalid="hasFieldError('discountPercent')"
          @input="localErrors.discountPercent = ''"
        />
        <small v-if="hasFieldError('discountPercent')" class="error">
          {{ getFieldError('discountPercent') }}
        </small>
      </div>

      <div class="row">
        <Checkbox
          v-model="isActive"
          inputId="discount-active"
          binary
        />
        <label for="discount-active">Активна</label>
      </div>
    </div>

    <template #footer>
      <Button
        label="Отмена"
        severity="secondary"
        outlined
        :disabled="props.submitting"
        @click="emit('close')"
      />
      <Button
        :label="submitLabel"
        :loading="props.submitting"
        :disabled="props.submitting"
        @click="submitForm"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.error {
  color: #dc2626;
  font-size: 12px;
}

:deep(.p-autocomplete),
:deep(.p-autocomplete-input),
:deep(.p-inputnumber-input) {
  width: 100%;
  border-radius: 12px;
}

:deep(.p-autocomplete.p-invalid .p-autocomplete-input),
:deep(.p-inputnumber-input.p-invalid) {
  border-color: #dc2626;
}
</style>
